// ═══════════════════════════════════════════════════════════════
// spark_viewer.js — Viewer partagé, importable par chaque page
// Usage : import { initViewer } from './spark_viewer.js';
//         initViewer(CONFIG);
// ═══════════════════════════════════════════════════════════════

import * as THREE from "three";
import { SplatMesh } from "@sparkjsdev/spark";
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { GUI } from "lil-gui";

export async function initViewer(viewerConfig = {}) {

  // ── Résolution des variables de config ──────────────────────
  const lieuCible  = viewerConfig.lieuCible  || document.title;
  const splatURL   = viewerConfig.modelURL   || null;
  const meshURL    = viewerConfig.meshURL    || null;
  const meshPropsURL = viewerConfig.meshPropsURL || null;
  const planURL    = viewerConfig.planURL    || null;
  const curvesURL     = viewerConfig.curvesURL     || null;

  // Per-model configuration arrays (indexed by model index)
  const modelPosX      = viewerConfig.modelPosX      || [];
  const modelPosY      = viewerConfig.modelPosY      || [];
  const modelPosZ      = viewerConfig.modelPosZ      || [];
  const modelRotXDeg   = viewerConfig.modelRotXDeg   || [];
  const modelRotYDeg   = viewerConfig.modelRotYDeg   || [];
  const modelRotZDeg   = viewerConfig.modelRotZDeg   || [];
  const modelScale     = viewerConfig.modelScale     || [];
  const modelLocalOffX = viewerConfig.modelLocalOffX || [];
  const modelLocalOffY = viewerConfig.modelLocalOffY || [];
  const modelLocalOffZ = viewerConfig.modelLocalOffZ || [];

  const showPlan = viewerConfig.showPlan || false;
  const bgColor = viewerConfig.bgColor || "#000000";
  const planMeshInFront = viewerConfig.planMeshInFront || false;
  const planMaterialOpacity = viewerConfig.planMaterialOpacity !== undefined ? viewerConfig.planMaterialOpacity : 1.0;
  const planScale = viewerConfig.planScale || 1.0;
  const planPosX = viewerConfig.planPosX || 0;
  const planPosY = viewerConfig.planPosY || 0;
  const planPosZ = viewerConfig.planPosZ || 0;
  const planRotX = viewerConfig.planRotX || 0;
  const planRotY = viewerConfig.planRotY || 0;
  const planRotZ = viewerConfig.planRotZ || 0;

if (viewerConfig.enableProps !== undefined && viewerConfig.enableProps === true) {
  const showProps = viewerConfig.showProps || true;
  const propsPosX = viewerConfig.propsPosX ?? 0;
  const propsPosY = viewerConfig.propsPosY ?? 0;
  const propsPosZ = viewerConfig.propsPosZ ?? 0;
  const propsRotX = viewerConfig.propsRotX ?? 0;
  const propsRotY = viewerConfig.propsRotY ?? 0;
  const propsRotZ = viewerConfig.propsRotZ ?? 0;
  const propsScale = viewerConfig.propsScale ?? 1.0;
}

  const showInterface = viewerConfig.showInterface !== undefined ? viewerConfig.showInterface : true;
  const showCross = viewerConfig.showCross !== undefined ? viewerConfig.showCross : true;

  // Curves configuration

  const showCurves = viewerConfig.showCurves || false;
  const curvesWidth = viewerConfig.curvesWidth || 1;  // épaisseur des courbes en pixels écran (indépendant du zoom)  
  const elevation_step = viewerConfig.elevation_step !== undefined ? viewerConfig.elevation_step : 10;
  const curves_scale = viewerConfig.curves_scale !== undefined ? viewerConfig.curves_scale : 1;
  const curvesPosX = viewerConfig.curvesPosX || 0;
  const curvesPosY = viewerConfig.curvesPosY || 0;
  const curvesPosZ = viewerConfig.curvesPosZ || 0;
  const curvesRotX = viewerConfig.curvesRotX || 0;
  const curvesRotY = viewerConfig.curvesRotY || 0;
  const curvesRotZ = viewerConfig.curvesRotZ || 0;


  // Helper function to safely access per-model config arrays with defaults
  function getModelConfig(index, configArray, defaultValue) {
    return configArray && configArray[index] !== undefined ? configArray[index] : defaultValue;
  }

  // ── Scène ────────────────────────────────────────────────────
  const scene  = new THREE.Scene();
  scene.background = new THREE.Color(bgColor);
  const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);

  const renderer = new THREE.WebGLRenderer({ antialias: viewerConfig.antialias || false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);

  // fps 
  const stats = new Stats();
  document.body.appendChild(stats.dom);
  stats.dom.style.display = "block";

  // ── Contrôles ────────────────────────────────────────────────
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(
    viewerConfig.camTargetX ?? 0.2,
    viewerConfig.camTargetY ?? 0,
    viewerConfig.camTargetZ ?? 0
  );
  controls.minDistance  = viewerConfig.camMinDistance ?? 0.1;
  controls.maxDistance  = viewerConfig.camMaxDistance ?? 10;
  controls.enabled      = true;
  controls.rotateSpeed  = 0.5;
  controls.enableDamping  = true;
  controls.dampingFactor  = viewerConfig.camDampingFactor || 0.08;
  controls.zoomToCursor   = true;

  camera.position.set(
    viewerConfig.camPosX ?? 0,
    viewerConfig.camPosY ?? 0,
    viewerConfig.camPosZ ?? 1
  );
  camera.rotation.set(
    viewerConfig.camRotX ?? 0,
    viewerConfig.camRotY ?? 0,
    viewerConfig.camRotZ ?? 0
  );
  controls.update();

  // ── Splat ────────────────────────────────────────────────────────────────────────────────
  const modeles = [];
  const pivotGroups = [];
  const crossGroups = [];

  splatURL.forEach((url, index) => {
    // Visual cross — marks the current origin point in the scene
    const crossSize = 5;
    const crossGroup = new THREE.Group();
    [
      [[-crossSize,0,0],[crossSize,0,0], 0xff4444],
      [[0,-crossSize,0],[0,crossSize,0], 0x44ff44],
      [[0,0,-crossSize],[0,0,crossSize], 0x4444ff]
    ].forEach(([p1, p2, color]) => {
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(...p1), new THREE.Vector3(...p2)
      ]);
      crossGroup.add(new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: true })));
    });
    crossGroup.renderOrder = 1;
    crossGroup.visible = showCross;
    scene.add(crossGroup);
    crossGroups.push(crossGroup);

    // Pivot group — model is parented here; all rotations/scales happen around its local origin
    const pivotGroup = new THREE.Group();
    scene.add(pivotGroup);
    pivotGroups.push(pivotGroup);

    const modele = new SplatMesh({ url });
    modele.quaternion.set(1, 0, 0, 0);
    modele.renderOrder = 0;
    pivotGroup.add(modele);
    modele.visible = (index === 0);
    modeles.push(modele);

    // Apply per-model configuration from viewerConfig arrays
    const cfgPosX = getModelConfig(index, modelPosX, 0);
    const cfgPosY = getModelConfig(index, modelPosY, 0);
    const cfgPosZ = getModelConfig(index, modelPosZ, 0);
    const cfgRotXDeg = getModelConfig(index, modelRotXDeg, 0);
    const cfgRotYDeg = getModelConfig(index, modelRotYDeg, 0);
    const cfgRotZDeg = getModelConfig(index, modelRotZDeg, 0);
    const cfgScale = getModelConfig(index, modelScale, 1);
    const cfgLocalOffX = getModelConfig(index, modelLocalOffX, 0);
    const cfgLocalOffY = getModelConfig(index, modelLocalOffY, 0);
    const cfgLocalOffZ = getModelConfig(index, modelLocalOffZ, 0);

    // Apply transforms to model and pivot
    pivotGroup.position.set(cfgPosX, cfgPosY, cfgPosZ);
    pivotGroup.rotation.order = 'YXZ';
    pivotGroup.rotation.set(
      cfgRotXDeg * (Math.PI / 180),
      cfgRotYDeg * (Math.PI / 180),
      cfgRotZDeg * (Math.PI / 180)
    );
    pivotGroup.scale.set(cfgScale, cfgScale, cfgScale);
    modele.position.set(cfgLocalOffX, cfgLocalOffY, cfgLocalOffZ);
  });

  // ── Mesh optionnel ───────────────────────────────────────────
  if (meshURL !== null) {
    const loader    = new GLTFLoader();
    const puitsMesh = await loader.loadAsync(meshURL);
    const wfMaterial = new THREE.MeshBasicMaterial({ color: 0xFFAA55, wireframe: true });
    puitsMesh.scene.traverse((child) => {
      if (child.isMesh) child.material = wfMaterial;
    });
    scene.add(puitsMesh.scene);
  }

  // ── Mesh props optionnel (voitures etc)───────────────────────
  
  let meshProps = null;
  if (meshPropsURL !== null) {
    const loader    = new GLTFLoader();
    const propsMaterial = new THREE.MeshLambertMaterial({ color: 0xFFFFFF, transparent: true, opacity: 1.0 });
    const gltf = await loader.loadAsync(meshPropsURL);
      meshProps = gltf.scene;      
    meshProps.name = "meshProps";
    meshProps.visible = viewerConfig.showProps !== undefined ? viewerConfig.showProps : true;
    meshProps.position.set(viewerConfig.propsPosX ?? 0, viewerConfig.propsPosY ?? 0, viewerConfig.propsPosZ ?? 0);
    meshProps.scale.set(viewerConfig.propsScale ?? 1, viewerConfig.propsScale ?? 1, viewerConfig.propsScale ?? 1);
    scene.add( new THREE.DirectionalLight( 0xffffff, 2 ) );
		scene.add( new THREE.AmbientLight( 0xffffff ) );
    scene.add(meshProps);
  }

  // ── Plan SVG ─────────────────────────────────────────────────
    let planMesh;
  if (planURL !== null) {
  async function loadSVGPlan(svgURL) {
    const response = await fetch(svgURL);
    const svgText  = await response.text();

    const parser     = new DOMParser();
    const svgDoc     = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    let width  = parseFloat(svgElement.getAttribute('width'))  || 1024;
    let height = parseFloat(svgElement.getAttribute('height')) || 1024;

    if (!svgElement.getAttribute('width')) {
      const viewBox = svgElement.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(' ');
        width  = parseFloat(parts[2]) || 1024;
        height = parseFloat(parts[3]) || 1024;
      }
    }

    const canvasWidth  = width  * 4;
    const canvasHeight = height * 4;

    const canvas = document.createElement('canvas');
    canvas.width  = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const img  = new Image();
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const url  = URL.createObjectURL(blob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const texture = new THREE.CanvasTexture(canvas);
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearFilter;

      const planGeometry = new THREE.PlaneGeometry(184.25, 56);
      const planMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1
      });

      const planMeshRenderOrder = -200;
      planMesh = new THREE.Mesh(planGeometry, planMaterial);
      planMesh.position.set(-0.9, -1.5, 34.2);
      planMesh.rotation.set(-87 * (Math.PI / 180), 0, 96 * (Math.PI / 180));
      planMesh.renderOrder = planMeshRenderOrder;
      planMesh.visible = showPlan;
      scene.add(planMesh);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  }
  loadSVGPlan(planURL);
  }

if (viewerConfig.enableCurves !== undefined && viewerConfig.enableCurves === true) {

  // ── Courbes topographiques ──────────────────────────────────
let courbes_elevation_step = null;

// Function to update elevation step and recalculate layer positions
function updateElevationStep(value) {
  courbes_elevation_step = value;
  // Update all layer positions based on new elevation step
  plan_courbes.children.forEach((grp, index) => {
    grp.position.z = index * courbes_elevation_step;
  });
}

//   topo_materials['#cd9cff'].color.set( 0xffffff );  // change toutes les lignes violettes
//   topo_materials['#cd9cff'].linewidth = 2;           // change l'épaisseur
const topo_materials = {};

function getMaterial( colorStyle ) {
    if ( ! topo_materials[ colorStyle ] ) {
        topo_materials[ colorStyle ] = new LineMaterial( {
            color:     new THREE.Color( colorStyle ),
            linewidth: curvesWidth,       // pixels écran (indépendant du zoom)
            resolution: new THREE.Vector2( window.innerWidth, window.innerHeight ),
        } );
    }
    return topo_materials[ colorStyle ];
}
// Mettre à jour la résolution de tous les matériaux lors d'un resize :
//   window.addEventListener( 'resize', updateTopoResolution );
function updateTopoResolution() {
    for ( const mat of Object.values( topo_materials ) ) {
        mat.resolution.set( window.innerWidth, window.innerHeight );
    }
}

// ── Groupe racine ─────────────────────────────────────────────────────────────
const plan_courbes = new THREE.Group();
  plan_courbes.name = 'plan_courbes';
  plan_courbes.visible = showCurves;
  plan_courbes.scale.setScalar( curves_scale );
  plan_courbes.position.set(curvesPosX, curvesPosY, curvesPosZ);
  plan_courbes.rotation.set(curvesRotX * (Math.PI / 180), curvesRotY * (Math.PI / 180), curvesRotZ * (Math.PI / 180));
//plan_courbes.rotation.x = Math.PI / 2;    // +π/2 : face visible vers le haut

const loader = new SVGLoader();
const SVGdata = await loader.loadAsync( curvesURL );

// SVGLoader retourne data.paths dans l'ordre du document.
// Chaque path expose :
//   path.userData.node   → l'élément SVG DOM correspondant
//   path.userData.style  → style résolu (stroke, fill, strokeWidth …)
//   path.color           → THREE.Color du stroke
//   path.subPaths        → tableau de CurvePath (un par sous-chemin M…Z)

// ── Regroupement par calque topographique ─────────────────────────────────────
// On remonte le DOM pour trouver le groupe racine ancêtre dont l'id
// correspond au pattern _81 / _81.5 / _92_5 etc.
const TOPO_ID_RE = /^_\d+[._]?\d*$/;   // ex. _81  _81.5  _92_5

function findTopoAncestor( node ) {
    let el = node;
    while ( el && el.tagName !== 'svg' ) {
        if ( el.id && TOPO_ID_RE.test( el.id ) ) return el;
        el = el.parentElement;
    }
    return null;
}

// Valeur numérique du niveau  "_81.5" → 81.5  |  "_92_5" → 92.5
function levelValue( id ) {
    return parseFloat( id.replace( /^_/, '' ).replace( '_', '.' ) ) || 0;
}

// Collecte : Map  groupId → { name, level, paths[] }
const layerMap = new Map();

for ( const path of SVGdata.paths ) {
    const node = path.userData?.node;
    if ( ! node ) continue;

    const ancestor = findTopoAncestor( node );
    if ( ! ancestor ) continue;    // hors calque topo (cadre, labels…)

    const id = ancestor.id;
    if ( ! layerMap.has( id ) ) {
        layerMap.set( id, {
            id,
            name:  ancestor.getAttribute( 'data-name' ) || id.replace( /^_/, '' ),
            level: levelValue( id ),
            paths: [],
        } );
    }
    layerMap.get( id ).paths.push( path );
}
// ── Construction des groupes Three.js ─────────────────────────────────────────
// Tri DÉCROISSANT : avec rotation.x = +π/2 l'axe Z local pointe vers le bas
// du plan, donc les niveaux élevés doivent avoir un Z plus grand (= index 0
// pour le niveau le plus haut) pour apparaître au-dessus des niveaux bas.
const layers = [ ...layerMap.values() ].sort( ( a, b ) => b.level - a.level );

for ( let i = 0; i < layers.length; i++ ) {
    const layer = layers[ i ];

    const grp = new THREE.Group();
    grp.name = layer.name;
    grp.position.z = i * courbes_elevation_step;   // élévation relative

    for ( const path of layer.paths ) {

        // Couleur du stroke – SVGLoader résout la cascade CSS
        const style    = path.userData.style;
        const colorCSS = style?.stroke && style.stroke !== 'none'
                         ? style.stroke
                         : '#' + path.color.getHexString();

        const material = getMaterial( colorCSS );

        // Un Line2 par sous-chemin (évite de relier des polylines disjointes)
        for ( const subPath of path.subPaths ) {
            const pts = subPath.getPoints();   // Vector2[]
            if ( pts.length < 2 ) continue;

            // LineGeometry attend un Float32Array plat  [x,y,z, x,y,z …]
            const positions = new Float32Array( pts.length * 3 );
            for ( let j = 0; j < pts.length; j++ ) {
                positions[ j * 3     ] = pts[ j ].x;
                positions[ j * 3 + 1 ] = pts[ j ].y;
                positions[ j * 3 + 2 ] = 0;
            }

            const geometry = new LineGeometry();
            geometry.setPositions( positions );
            grp.add( new Line2( geometry, material ) );
        }
    }
    plan_courbes.add( grp );
}
    scene.add( plan_courbes );


}

  // ── GUI ──────────────────────────────────────────────────────
  let preset = {};

  const gui = new GUI({
    title: "Paramètres",
    container: document.getElementById("main-gui")
  });
    gui.close();

// cache l'interface et les fps si showInterface est false dans la config
    if (showInterface === false) {
      gui.hide();
    stats.dom.style.display = "none";}
      else {(gui.show());      
    }

  const guiOptions = {
    showInterface: showInterface,
    stats: true,
    bgColor: bgColor,
    showLabels: true,
    labelSize: 1.0,
    labelOpacity: 1.0,
    distanceThreshold: false,
    savePreset() { preset = gui.save(); },
    loadPreset() { gui.load(preset); }
  };

  gui.addColor(guiOptions, "bgColor").name("Couleur de fond").onChange((value) => {
    scene.background = new THREE.Color(value);
  });

  gui.add(guiOptions, "stats").name("Afficher les FPS").onChange((value) => {
    stats.dom.style.display = value ? "block" : "none";
  });  

  const cameraFolder = gui.addFolder("Camera");
  cameraFolder.add(camera, "fov", 1, 179, 0.005).name("Champs de vision").listen().onChange(() => {
    camera.updateProjectionMatrix();
  });
  cameraFolder.add(guiOptions, "distanceThreshold").name("Déverrouiller la distance max").onChange(() => {
    controls.maxDistance = guiOptions.distanceThreshold ? 999999 : 100;
  });

  const cameraPose = cameraFolder.addFolder("Pose Caméra");
  cameraPose.add(camera.position, "x", -100, 100, 0.01).name("X").listen().decimals(3);
  cameraPose.add(camera.position, "y", -100, 100, 0.01).name("Y").listen().decimals(3);
  cameraPose.add(camera.position, "z", -100, 100, 0.01).name("Z").listen().decimals(3);

  const cameraSpecs = cameraPose.addFolder("Camera Specs");
  cameraSpecs.add(camera.rotation, "x", -Math.PI, Math.PI, 0.01).name("RotateX").listen().decimals(3).disable();
  cameraSpecs.add(camera.rotation, "y", -Math.PI, Math.PI, 0.01).name("RotateY").listen().decimals(3).disable();
  cameraSpecs.add(camera.rotation, "z", -Math.PI, Math.PI, 0.01).name("RotateZ").listen().decimals(3).disable();
  cameraSpecs.add(controls.target, "x", -100, 100, 0.01).name("Target X").listen().decimals(3).disable();
  cameraSpecs.add(controls.target, "y", -100, 100, 0.01).name("Target Y").listen().decimals(3).disable();
  cameraSpecs.add(controls.target, "z", -100, 100, 0.01).name("Target Z").listen().decimals(3).disable();
  cameraSpecs.close();
  cameraPose.close();

  const planFolder = gui.addFolder("Plan");
  planFolder.add(viewerConfig, "showPlan").name("Visible").onChange(updatePlanVisibility);
  planFolder.add(viewerConfig, "planMeshInFront").name("Plan en superposition").onChange(isPlanMeshInFront);
  planFolder.add(viewerConfig, "planMaterialOpacity", 0, 1, 0.01).name("Opacité").onChange((value) => {
    if (planMesh) planMesh.material.opacity = value;
  });

  const planTransform = planFolder.addFolder("Transformer");
  planTransform.close();
  planTransform.add(viewerConfig, "planScale", 0.1, 5, 0.01).name("Échelle").listen().onChange((value) => {
    if (planMesh) { planMesh.scale.x = value; planMesh.scale.y = value; }
  });
  planTransform.add(viewerConfig, "planPosX", -10, 10, 0.01).name("Pos X").listen().onChange((v) => { if (planMesh) planMesh.position.x = v; });
  planTransform.add(viewerConfig, "planPosY", -10, 10, 0.01).name("Pos Y").listen().onChange((v) => { if (planMesh) planMesh.position.y = v; });
  planTransform.add(viewerConfig, "planPosZ", -10, 10, 0.01).name("Pos Z").listen().onChange((v) => { if (planMesh) planMesh.position.z = v; });
  planTransform.add(viewerConfig, "planRotX", -180, 180, 1).name("Rot X (deg)").listen().onChange((v) => { if (planMesh) planMesh.rotation.x = v * (Math.PI / 180); });
  planTransform.add(viewerConfig, "planRotY", -180, 180, 1).name("Rot Y (deg)").listen().onChange((v) => { if (planMesh) planMesh.rotation.y = v * (Math.PI / 180); });
  planTransform.add(viewerConfig, "planRotZ", -180, 180, 1).name("Rot Z (deg)").listen().onChange((v) => { if (planMesh) planMesh.rotation.z = v * (Math.PI / 180); });
  planTransform.add({ reset: resetPlanTransform }, "reset").name("Reset Position/Rotation");

  function resetPlanTransform() {
    guiOptions.planPosX = -0.9; guiOptions.planPosY = -1.5; guiOptions.planPosZ = 34.2;
    guiOptions.planRotX = -87;  guiOptions.planRotY = 0;    guiOptions.planRotZ = 96;
    if (planMesh) {
      planMesh.position.set(guiOptions.planPosX, guiOptions.planPosY, guiOptions.planPosZ);
      planMesh.rotation.set(
        guiOptions.planRotX * (Math.PI / 180),
        guiOptions.planRotY * (Math.PI / 180),
        guiOptions.planRotZ * (Math.PI / 180)
      );
    }
  }
  planFolder.close();

  if (viewerConfig.enableProps !== undefined && viewerConfig.enableProps === true) {
    const propsFolder = gui.addFolder("Props");
  propsFolder.add(viewerConfig, "showProps").name("Visible").onChange(updatePropsVisibility);

  const propsTransform = propsFolder.addFolder("Transformer");
  propsTransform.close();
  propsTransform.add(viewerConfig, "propsScale", 0.1, 5, 0.0001).name("Échelle").listen().onChange((value) => {
    if (meshProps) { meshProps.scale.x = value; meshProps.scale.y = value; meshProps.scale.z = value; }
  });
  propsTransform.add(viewerConfig, "propsPosX", -100, 100, 0.001).name("Pos X").listen().onChange((v) => { if (meshProps) meshProps.position.x = v; });
  propsTransform.add(viewerConfig, "propsPosY", -100, 100, 0.001).name("Pos Y").listen().onChange((v) => { if (meshProps) meshProps.position.y = v; });
  propsTransform.add(viewerConfig, "propsPosZ", -100, 100, 0.01).name("Pos Z").listen().onChange((v) => { if (meshProps) meshProps.position.z = v; });
  propsTransform.add(viewerConfig, "propsRotX", -180, 180, 1).name("Rot X (deg)").listen().onChange((v) => { if (meshProps) meshProps.rotation.x = v * (Math.PI / 180); });
  propsTransform.add(viewerConfig, "propsRotY", -180, 180, 1).name("Rot Y (deg)").listen().onChange((v) => { if (meshProps) meshProps.rotation.y = v * (Math.PI / 180); });
  propsTransform.add(viewerConfig, "propsRotZ", -180, 180, 1).name("Rot Z (deg)").listen().onChange((v) => { if (meshProps) meshProps.rotation.z = v * (Math.PI / 180); });
  propsTransform.add({ reset: resetPropsTransform }, "reset").name("Reset Position/Rotation");

  function resetPropsTransform() {
    guiOptions.propsPosX = -0.9; guiOptions.propsPosY = -1.5; guiOptions.propsPosZ = 34.2;
    guiOptions.propsRotX = -87;  guiOptions.propsRotY = 0;    guiOptions.propsRotZ = 96;
    if (meshProps) {
      meshProps.position.set(guiOptions.propsPosX, guiOptions.propsPosY, guiOptions.propsPosZ);
      meshProps.rotation.set(
        guiOptions.propsRotX * (Math.PI / 180),
        guiOptions.propsRotY * (Math.PI / 180),
        guiOptions.propsRotZ * (Math.PI / 180)
      );
    }
  }
  propsFolder.close();
}
if (viewerConfig.enableCurves !== undefined && viewerConfig.enableCurves === true) {

  const courbesFolder = gui.addFolder("Courbes topographiques");
    courbesFolder.add(viewerConfig, "showCurves").name("Visible").onChange(updateCurvesVisibility);

    const courbesTransform = courbesFolder.addFolder("Options courbes");
    courbesTransform.add(viewerConfig, "curvesWidth", 1, 5, 0.5).name("Épaisseur (px)").onChange((value) => {if (plan_courbes) plan_courbes.traverse((child) => { if (child.material) child.material.linewidth = value; }); });
    courbesTransform.add(viewerConfig, "curves_scale", 0.01, 1, 0.000001).name("Échelle des courbes").listen().onChange((value) => {
    if (plan_courbes) plan_courbes.scale.setScalar(value);
  });
      courbesTransform.add(viewerConfig, "elevation_step", 0.01, 100, 0.0001).name("Écart courbes (m)").listen().onChange((value) => { updateElevationStep(value); });
      courbesTransform.add(viewerConfig, "curvesPosX", -300, 300, 0.0001).name("Pos X").listen().onChange((v) => { if (plan_courbes) plan_courbes.position.x = v; });
      courbesTransform.add(viewerConfig, "curvesPosY", -300, 300, 0.0001).name("Pos Y").listen().onChange((v) => { if (plan_courbes) plan_courbes.position.y = v; });
      courbesTransform.add(viewerConfig, "curvesPosZ", -300, 300, 0.0001).name("Pos Z").listen().onChange((v) => { if (plan_courbes) plan_courbes.position.z = v; });
      courbesTransform.add(viewerConfig, "curvesRotX", -180, 180, 0.0001).name("Rot X (deg)").listen().onChange((v) => { if (plan_courbes) plan_courbes.rotation.x = v * (Math.PI / 180); });
      courbesTransform.add(viewerConfig, "curvesRotY", -180, 180, 0.0001).name("Rot Y (deg)").listen().onChange((v) => { if (plan_courbes) plan_courbes.rotation.y = v * (Math.PI / 180); });
      courbesTransform.add(viewerConfig, "curvesRotZ", -180, 180, 0.0001).name("Rot Z (deg)").listen().onChange((v) => { if (plan_courbes) plan_courbes.rotation.z = v * (Math.PI / 180); });
  
      courbesTransform.close();

  courbesFolder.close();
}
  const modelsFolder = gui.addFolder("Modèles");

  modeles.forEach((modele, index) => {
    const modelName = splatURL[index].split('/').pop().slice(0, 20);
    const isFirstModel = index === 0;
    const crossGroup = crossGroups[index];
    const pivotGroup = pivotGroups[index];

    modele.visible = isFirstModel;

    const modelOptions = { visible: isFirstModel };
    modelsFolder.add(modelOptions, 'visible')
      .name(`${index} ${modelName}`)
      .onChange((value) => { modele.visible = value; });

    const modelTransformFolder = modelsFolder.addFolder(`${index} ${modelName} - Transform`);

    // Staged origin position — cross moves here; Appliquer commits it as the pivot
    const origineOptions = { x: getModelConfig(index, modelPosX, 0), y: getModelConfig(index, modelPosY, 0), z: getModelConfig(index, modelPosZ, 0), showCross: showCross };

    const modelGuiOptions = {
      scale: getModelConfig(index, modelScale, 1),
      posX: getModelConfig(index, modelPosX, 0), posY: getModelConfig(index, modelPosY, 0), posZ: getModelConfig(index, modelPosZ, 0),          // pivotGroup world position = current origin
      rotXDeg: getModelConfig(index, modelRotXDeg, 0), rotYDeg: getModelConfig(index, modelRotYDeg, 0), rotZDeg: getModelConfig(index, modelRotZDeg, 0), // pivotGroup rotation (around origin)
      localOffX: getModelConfig(index, modelLocalOffX, 0), localOffY: getModelConfig(index, modelLocalOffY, 0), localOffZ: getModelConfig(index, modelLocalOffZ, 0), // modele.position in pivotGroup local space (needed for preset)
      reset: () => {
        modelGuiOptions.scale = 1;
        modelGuiOptions.posX = 0; modelGuiOptions.posY = 0; modelGuiOptions.posZ = 0;
        modelGuiOptions.rotXDeg = 0; modelGuiOptions.rotYDeg = 0; modelGuiOptions.rotZDeg = 0;
        modelGuiOptions.localOffX = 0; modelGuiOptions.localOffY = 0; modelGuiOptions.localOffZ = 0;
        origineOptions.x = 0; origineOptions.y = 0; origineOptions.z = 0;
        crossGroup.position.set(0, 0, 0);
        pivotGroup.position.set(0, 0, 0);
        pivotGroup.rotation.set(0, 0, 0);
        pivotGroup.scale.set(1, 1, 1);
        modele.position.set(0, 0, 0);
        modele.quaternion.set(1, 0, 0, 0);
      }
    };

    // Scale — scales around origin
    modelTransformFolder.add(modelGuiOptions, 'scale', 0.1, 5, 0.0001).name('Scale').listen().onChange((v) => {
      pivotGroup.scale.set(v, v, v);
    });
    // Position — moves origin+model together (pivotGroup world pos); cross follows
    modelTransformFolder.add(modelGuiOptions, 'posX', -100, 100, 0.0001).name('Pos X').listen().onChange((v) => {
      pivotGroup.position.x = v;
      crossGroup.position.x = v;
      origineOptions.x = v;
    });
    modelTransformFolder.add(modelGuiOptions, 'posY', -100, 100, 0.0001).name('Pos Y').listen().onChange((v) => {
      pivotGroup.position.y = v;
      crossGroup.position.y = v;
      origineOptions.y = v;
    });
    modelTransformFolder.add(modelGuiOptions, 'posZ', -100, 100, 0.0001).name('Pos Z').listen().onChange((v) => {
      pivotGroup.position.z = v;
      crossGroup.position.z = v;
      origineOptions.z = v;
    });
    // Rotation — rotates around the origin point
    modelTransformFolder.add(modelGuiOptions, 'rotXDeg', -180, 180, 0.001).name('Rot X (deg)').listen().onChange((v) => {
      pivotGroup.rotation.order = 'YXZ';
      pivotGroup.rotation.x = v * (Math.PI / 180);
    });
    modelTransformFolder.add(modelGuiOptions, 'rotYDeg', -180, 180, 0.001).name('Rot Y (deg)').listen().onChange((v) => {
      pivotGroup.rotation.order = 'YXZ';
      pivotGroup.rotation.y = v * (Math.PI / 180);
    });
    modelTransformFolder.add(modelGuiOptions, 'rotZDeg', -180, 180, 0.001).name('Rot Z (deg)').listen().onChange((v) => {
      pivotGroup.rotation.order = 'YXZ';
      pivotGroup.rotation.z = v * (Math.PI / 180);
    });

    // Origine subfolder — drag the cross to a new pivot point, click Appliquer to commit it
    const origineFolder = modelTransformFolder.addFolder('Origine');
    origineFolder.add(origineOptions, 'showCross').name('Afficher croix').onChange((v) => { crossGroup.visible = v; });
    origineFolder.add(origineOptions, 'x', -100, 100, 0.0001).name('X').listen().onChange((v) => { crossGroup.position.x = v; });
    origineFolder.add(origineOptions, 'y', -100, 100, 0.0001).name('Y').listen().onChange((v) => { crossGroup.position.y = v; });
    origineFolder.add(origineOptions, 'z', -100, 100, 0.0001).name('Z').listen().onChange((v) => { crossGroup.position.z = v; });
    origineFolder.add({
      apply: () => {
        // Snapshot model's current world position (it must stay visually in place)
        const modelWorldPos = new THREE.Vector3();
        modele.getWorldPosition(modelWorldPos);

        // Move the pivot to where the cross is
        pivotGroup.position.set(origineOptions.x, origineOptions.y, origineOptions.z);
        pivotGroup.updateMatrixWorld(true);

        // Recompute model's local position so it stays visually in place
        modele.position.copy(pivotGroup.worldToLocal(modelWorldPos));

        // Sync GUI sliders and local offset (needed for correct preset save/load)
        modelGuiOptions.posX = pivotGroup.position.x;
        modelGuiOptions.posY = pivotGroup.position.y;
        modelGuiOptions.posZ = pivotGroup.position.z;
        modelGuiOptions.localOffX = modele.position.x;
        modelGuiOptions.localOffY = modele.position.y;
        modelGuiOptions.localOffZ = modele.position.z;
      }
    }, 'apply').name('Appliquer');

    // Hidden sub-folder: stores model's local offset within pivotGroup for preset restore
    const localOffFolder = origineFolder.addFolder('Décalage local');
    localOffFolder.add(modelGuiOptions, 'localOffX', -500, 500, 0.0001).name('X').listen().onChange((v) => { modele.position.x = v; });
    localOffFolder.add(modelGuiOptions, 'localOffY', -500, 500, 0.0001).name('Y').listen().onChange((v) => { modele.position.y = v; });
    localOffFolder.add(modelGuiOptions, 'localOffZ', -500, 500, 0.0001).name('Z').listen().onChange((v) => { modele.position.z = v; });
    localOffFolder.close();
    origineFolder.close();

    modelTransformFolder.add(modelGuiOptions, 'reset').name('Mettre à zéro');
    modelTransformFolder.close();
 modelsFolder.close();   
  });

  // Exporter preset
  gui.add({ export: exportPreset }, "export").name("Exporter Preset");
  function exportPreset() {
    preset = gui.save();
    const json = JSON.stringify(preset, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    const now  = new Date();
    const yyyy = now.getFullYear();
    const mm   = String(now.getMonth() + 1).padStart(2, '0');
    const dd   = String(now.getDate()).padStart(2, '0');
    const hh   = String(now.getHours()).padStart(2, '0');
    const min  = String(now.getMinutes()).padStart(2, '0');
    a.download = `preset-${lieuCible || 'scan'}-${yyyy}-${mm}-${dd}_${hh}-${min}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Importer preset
  gui.add({ import: importPreset }, "import").name("Importer Preset");
  function importPreset() {
    const input   = document.createElement('input');
    input.type    = 'file';
    input.accept  = 'application/json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          preset = JSON.parse(event.target.result);
          gui.load(preset);
        } catch (err) {
          console.error('Error importing preset:', err);
          alert('Failed to import preset. Check the file format.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  // Export PNG
  gui.add({ exportView: exportViewAsPNG }, "exportView").name("Exporter la vue en PNG");
  function exportViewAsPNG() {
    const origWidth  = renderer.domElement.width;
    const origHeight = renderer.domElement.height;
    renderer.setSize(origWidth * 2, origHeight * 2);
    labelRenderer.setSize(origWidth * 2, origHeight * 2);
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
    renderer.domElement.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href    = url;
      const now   = new Date();
      const year  = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day   = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const mins  = String(now.getMinutes()).padStart(2, '0');
      a.download = `view_${lieuCible}_${year}-${month}-${day}_${hours}_${mins}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      renderer.setSize(origWidth, origHeight);
      labelRenderer.setSize(origWidth, origHeight);
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
    }, 'image/png');
  }

  function updatePlanVisibility() {
    if (planMesh) planMesh.visible = viewerConfig.showPlan;
  }

    function updatePropsVisibility() {
    if (meshProps) meshProps.visible = viewerConfig.showProps;
  }
   
    function updateCurvesVisibility() {
    if (plan_courbes) plan_courbes.visible = viewerConfig.showCurves;
  }

  function isPlanMeshInFront (){ 
    if (planMesh) planMesh.renderOrder = viewerConfig.planMeshInFront ? 100 : -200;}


  // ── TransformWidget ──────────────────────────────────────────
  class TransformWidget {
    constructor(scene, camera, renderer, gui) {
      this.scene    = scene;
      this.camera   = camera;
      this.renderer = renderer;

      this.widgetGroup = new THREE.Group();
      this.widgetGroup.visible = false;
      this.widgetGroup.position.set(0, 0, 0);
      this.scene.add(this.widgetGroup);

      const axesHelper = new THREE.AxesHelper(1);
      this.widgetGroup.add(axesHelper);

      this.handle = document.createElement('div');
      Object.assign(this.handle.style, {
        position: 'absolute', width: '18px', height: '18px',
        borderRadius: '50%', background: 'rgba(255,0,0,0.8)',
        border: '2px solid #fff', boxSizing: 'border-box',
        pointerEvents: 'auto', cursor: 'grab', zIndex: '1001',
        transform: 'translate(-50%, -50%)', display: 'none'
      });
      document.body.appendChild(this.handle);

      this._raycaster = new THREE.Raycaster();
      this._plane     = new THREE.Plane();
      this._dragging  = false;
      this._pointerId = null;

      this.handle.addEventListener('pointerdown',  (e) => this._onHandlePointerDown(e));
      window.addEventListener('pointermove', (e) => this._onPointerMove(e));
      window.addEventListener('pointerup',   (e) => this._onPointerUp(e));

      this._updateLoop = () => {
        if (this.widgetGroup.visible) this._updateHandlePosition();
        this._rafId = requestAnimationFrame(this._updateLoop);
      };
      this._rafId = requestAnimationFrame(this._updateLoop);

      const toolsFolder = gui.addFolder('Tools');
      this.guiOptions = {
        antialias: viewerConfig.antialias || false,
        widgetEnabled: false,
        posX: 0, posY: 0, posZ: 0,
        coordDisplay: '0.00, 0.00, 0.00',
        copyCoordinates: () => this.copyCoordinates(),
        resetPosition:   () => this.resetPosition()
      };

      toolsFolder.add(this.guiOptions, "antialias").name("Antialiasing").disable();
      toolsFolder.add(this.guiOptions, 'widgetEnabled').name('Afficher Repère 3D').onChange((v) => this.setVisibility(v));

      const positionFolder = toolsFolder.addFolder('Position');
      positionFolder.add(this.guiOptions, 'posX', -100, 100, 0.1).name('X').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.add(this.guiOptions, 'posY', -100, 100, 0.1).name('Y').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.add(this.guiOptions, 'posZ', -100, 100, 0.1).name('Z').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.close();

      toolsFolder.add(this.guiOptions, 'coordDisplay').name('Coordonnées').listen();
      toolsFolder.add(this.guiOptions, 'copyCoordinates').name('Copier les coordonnées');
      toolsFolder.add(this.guiOptions, 'resetPosition').name('Réinitialiser');
      toolsFolder.close();
    }

    setVisibility(value) {
      this.widgetGroup.visible = value;
      this.handle.style.display = value ? 'block' : 'none';
      this.updateDisplay();
    }

    updateDisplay() {
      const pos = this.widgetGroup.position;
      this.guiOptions.posX = parseFloat(pos.x.toFixed(2));
      this.guiOptions.posY = parseFloat(pos.y.toFixed(2));
      this.guiOptions.posZ = parseFloat(pos.z.toFixed(2));
      this.guiOptions.coordDisplay = `${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}`;
    }

    updatePositionFromInputs() {
      this.widgetGroup.position.set(
        parseFloat(this.guiOptions.posX),
        parseFloat(this.guiOptions.posY),
        parseFloat(this.guiOptions.posZ)
      );
      this.updateDisplay();
    }

    resetPosition() {
      this.widgetGroup.position.set(0, 0, 0);
      this.updateDisplay();
    }

    copyCoordinates() {
      navigator.clipboard.writeText(this.guiOptions.coordDisplay)
        .then(() => console.log('Coordonnées copiées:', this.guiOptions.coordDisplay))
        .catch(err => console.error('Erreur lors de la copie:', err));
    }

    _getPointerNDC(e) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width)  * 2 - 1,
        -((e.clientY - rect.top)  / rect.height) * 2 + 1
      );
    }

    _onHandlePointerDown(e) {
      e.stopPropagation(); e.preventDefault();
      this._pointerId = e.pointerId;
      this.handle.setPointerCapture(this._pointerId);
      this.handle.style.cursor = 'grabbing';
      this._dragging = true;
      const camDir = new THREE.Vector3();
      this.camera.getWorldDirection(camDir).normalize();
      this._plane.setFromNormalAndCoplanarPoint(camDir, this.widgetGroup.position);
    }

    _onPointerMove(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      e.stopPropagation(); e.preventDefault();
      const ndc = this._getPointerNDC(e);
      this._raycaster.setFromCamera(ndc, this.camera);
      const intersect = new THREE.Vector3();
      if (this._raycaster.ray.intersectPlane(this._plane, intersect)) {
        this.widgetGroup.position.copy(intersect);
        this.updateDisplay();
        this._updateHandlePosition();
      }
    }

    _onPointerUp(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      e.stopPropagation(); e.preventDefault();
      this.handle.releasePointerCapture(this._pointerId);
      this._pointerId = null;
      this._dragging  = false;
      this.handle.style.cursor = 'grab';
    }

    _updateHandlePosition() {
      const pos = this.widgetGroup.position.clone();
      pos.project(this.camera);
      this.handle.style.left = `${(pos.x *  0.5 + 0.5) * window.innerWidth}px`;
      this.handle.style.top  = `${(pos.y * -0.5 + 0.5) * window.innerHeight}px`;
    }
  }

  const widget = new TransformWidget(scene, camera, renderer, gui);

  // ── Données JSON + Labels ────────────────────────────────────
  const res  = await fetch('assets/_data/data_info_layer.json');
  const data = await res.json();
  console.log("JSON chargé");

  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(window.innerWidth, window.innerHeight);
  labelRenderer.domElement.style.position     = 'absolute';
  labelRenderer.domElement.style.top          = '0px';
  labelRenderer.domElement.style.pointerEvents = 'none';
  document.body.appendChild(labelRenderer.domElement);

  async function loadLabels(lieuCible) {
    const reponse        = await fetch('misc/templates/label.htm');
    const label_template = await reponse.text();
    const filtered       = data.filter(entry => entry.lieu === lieuCible);
    filtered.forEach(entry => createLabel(entry, label_template));
  }

  loadLabels(lieuCible);

  function createLabel(entry, template_html) {
    const label_import = document.createElement("div");
    label_import.innerHTML = template_html.trim();
    label_import.querySelector('.label_title').textContent = entry.titre;
    label_import.querySelector('.label_image').src = entry.image_label || '';
    const imgContainer = label_import.querySelector('.label_image_container');
    if (!entry.image_label) imgContainer.classList.add('hidden');
    else imgContainer.classList.remove('hidden');

    const posCible = new THREE.Vector3().fromArray(entry.coord_cible);
    const posLabel = new THREE.Vector3().fromArray(entry.coord_label);
    console.log("labels itérés");

    label_import.style.pointerEvents = 'auto';
    label_import.style.cursor        = 'pointer';
    const label_container = new CSS2DObject(label_import);
    label_container.position.copy(posLabel);

    label_import.addEventListener('click', () => {
      fillPopup(entry);
      popupEl.classList.add('open');
    });

    scene.add(label_container);

    const points      = [posCible, posLabel];
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff });
    scene.add(new THREE.Line(lineGeometry, lineMaterial));

    const circleGeometry = new THREE.SphereGeometry(0.02, 16, 16);
    const circleMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const circle = new THREE.Mesh(circleGeometry, circleMaterial);
    circle.position.copy(posCible);
    scene.add(circle);
  }

  // ── Popup ────────────────────────────────────────────────────
  let popupEl;
  fetch('misc/templates/popup.htm')
    .then(res => res.text())
    .then(html => {
      const popUpContainer = document.createElement('div');
      popUpContainer.innerHTML = html.trim();
      const styleEl = popUpContainer.querySelector('style');
      if (styleEl) document.head.appendChild(styleEl);
      popupEl = popUpContainer.querySelector('#popupContainer');
      document.body.appendChild(popupEl);

      popupEl.querySelector('#popupClose').addEventListener('click', () => {
        popupEl.classList.remove('open');
        popupEl.classList.add('collapsed');
      });
      popupEl.querySelector('#popupHeader').addEventListener('click', (e) => {
        if (e.target.id === 'popupClose') return;
        popupEl.classList.add('open');
        popupEl.classList.remove('collapsed');
      });
    });

  function fillPopup(entry) {
    if (!popupEl) return;
    popupEl.querySelector('#popupTitle').textContent    = entry.titre_complet;
    popupEl.querySelector('#popupDate').textContent     = entry.date;
    popupEl.querySelector('#popupSubTitle').textContent = entry.sous_titre;
    popupEl.querySelector('#popupText').textContent     = entry.contenu;
    popupEl.classList.add('open');
    popupEl.classList.remove('collapsed');
  }

  // ── Resize ───────────────────────────────────────────────────
  function onWindowResize() {
    const w = window.innerWidth, h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    labelRenderer.setSize(w, h);
    controls.update();
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
  }
  window.addEventListener('resize', onWindowResize, { passive: true });

  // ── Boucle d'animation ───────────────────────────────────────
  renderer.setAnimationLoop(function animate() {
    stats.begin();
    controls.update();
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
    stats.end();
  });
}
